import { FC, MouseEvent, useState } from 'react';

import { useStopMeasureWhenRendered } from '@mint-lib/datadog';
import styled, { css } from '@mint-lib/styled';

import { Box, Icon, IconButton, CircularProgress as Loader } from '@myn/mui';

import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { TableEditCellProps } from './types.js';
import useEditCellVariant from './useEditCellVariant.jsx';
import { getContainerMaxWidth, getWrapperPaddingValue } from './utils.js';

const TableEditCell: FC<TableEditCellProps> = (props) => {
  const {
    scale,
    isDeleteButtonShown = true,
    deleteValueFunc,
    showEditModeFunc,
    startMeasurePerformance,
    value,
    variant,
    disabled = false,
  } = props;

  const { renderFormattedField } = useEditCellVariant(props);

  const isValidValue = !!value || value === 0;
  const isDeleteButtonVisible =
    isValidValue && isDeleteButtonShown && !disabled;
  const isCalendarIconShown = variant === 'date' && isValidValue;
  const isGoalField = variant === 'goal';

  const [isLoading, setIsLoading] = useState(false);

  const handleClick = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (!isGoalField) {
      startMeasurePerformance(TRACKING_EVENTS.VALUE_INSERTION);
    }

    showEditModeFunc(e);
  };

  useStopMeasureWhenRendered(
    TRACKING_EVENTS.VALUE_INSERTION,
    {
      react: { component: 'TableEditCell' },
    },
    { shouldIgnoreRender: isGoalField },
  );

  const handleDeleteClick = async (e: MouseEvent<HTMLButtonElement>) => {
    setIsLoading(true);
    await deleteValueFunc(e);
    setIsLoading(false);
  };

  const deleteButton = isDeleteButtonVisible && (
    <StyledIconButton
      className="clear-button"
      data-testid="clear-button"
      variant="icon"
      color="primary"
      size="small"
      icon="Close"
      onClick={handleDeleteClick}
      sx={{
        zoom: scale,
      }}
    />
  );

  return (
    <Wrapper
      onClick={handleClick}
      $scale={scale}
      $isDeleteButtonShown={isDeleteButtonShown}
      $disabled={disabled}
    >
      {isLoading ? <Loader size={16} /> : deleteButton}
      <Container className="container" $scale={scale} $disabled={disabled}>
        {isCalendarIconShown && (
          <Icon
            component="Calendar"
            sx={{
              zoom: scale,
            }}
          />
        )}
        {renderFormattedField()}
      </Container>

      <StyledIconButton
        className="edit-button"
        data-testid="edit-button"
        variant="icon"
        color="primary"
        size="small"
        icon="Edit"
        onClick={handleClick}
        sx={{
          zoom: scale,
        }}
      />
    </Wrapper>
  );
};

export default TableEditCell;

const Wrapper = styled(Box)<{
  $scale: number;
  $isDeleteButtonShown: boolean;
  $disabled: boolean;
}>`
  ${({ $scale, $isDeleteButtonShown, $disabled, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${8 * $scale}px;
    width: 100%;
    height: 100%;
    padding: ${getWrapperPaddingValue(false, $scale)};
    cursor: default;
    overflow: hidden;

    ${!$disabled &&
    css`
      &:hover {
        padding: ${getWrapperPaddingValue(true, $scale)};
        background: ${theme.palette.ui.hoverCell};
        cursor: pointer;

        .container {
          max-width: ${getContainerMaxWidth($isDeleteButtonShown, $scale)};
        }

        .clear-button,
        .edit-button {
          display: block;
        }
      }
    `}
  `}
`;

const Container = styled(Box)<{
  $scale: number;
  $disabled: boolean;
}>`
  ${({ $scale, $disabled }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    gap: ${8 * $scale}px;
    max-width: 100%;
    overflow: hidden;

    span {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;

      font-size: ${0.875 * $scale}rem;
      line-height: ${$scale}rem;
      cursor: ${$disabled ? 'default' : 'pointer'};
    }
  `}
`;

const StyledIconButton = styled(IconButton)`
  display: none;
`;
